import React, { FC, useRef, useState } from 'react';
import { T } from 'src/shared/helpers';
import styled from 'styled-components';

export interface CollapsibleSectionProps {
    title: string;
    count: number;
    isBoolean?: boolean;
    defaultOpen?: boolean;
    forceOpen?: boolean;
    controlledOpen?: boolean;
    tall?: boolean;
    helpUrl?: string;
    onHelpClick?: () => void;
    onOpenChange?: (isOpen: boolean) => void;
    children: React.ReactNode;
}

const SectionContainer = styled.div`
    border-bottom: 1px solid #f3f4f6;

    &:last-child {
        border-bottom: none;
    }
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 24px;
    cursor: pointer;
    user-select: none;
    transition: background 0.15s;

    &:hover {
        background: #f9fafb;
    }
`;

const SectionLeft = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
`;

const SectionArrow = styled.span<{ $open: boolean }>`
    font-size: 12px;
    color: #9ca3af;
    transition: transform 0.2s;
    display: inline-block;
    transform: rotate(${(p) => (p.$open ? '90deg' : '0deg')});
`;

const SectionTitle = styled.span`
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #6b7280;
`;

const SectionCount = styled.span<{ $isZero?: boolean }>`
    font-size: 11px;
    font-weight: 600;
    padding: 1px 8px;
    border-radius: 10px;
    background: ${(p) => (p.$isZero ? '#f3f4f6' : '#dbeafe')};
    color: ${(p) => (p.$isZero ? '#6b7280' : '#1e40af')};
`;

const SectionBody = styled.div<{ $collapsed: boolean }>`
    display: ${(p) => (p.$collapsed ? 'none' : 'block')};
    padding: 0 24px 14px;
`;

const ItemsList = styled.div<{ $tall?: boolean }>`
    max-height: ${(p) => (p.$tall ? '300px' : '200px')};
    overflow-y: auto;
    border-radius: 8px;

    &::-webkit-scrollbar {
        width: 4px;
    }
    &::-webkit-scrollbar-track {
        background: transparent;
    }
    &::-webkit-scrollbar-thumb {
        background: #d1d5db;
        border-radius: 4px;
    }
`;

const FooterGuideLink = styled.a`
    display: block;
    font-size: 11px;
    color: #6b7280;
    text-decoration: none;
    padding: 6px 0 0;
    text-align: right;

    &:hover {
        color: #2563eb;
        text-decoration: underline;
    }
`;

export const CollapsibleSection: FC<CollapsibleSectionProps> = ({
    title,
    count,
    isBoolean,
    defaultOpen = false,
    forceOpen,
    controlledOpen,
    tall,
    helpUrl,
    onHelpClick,
    onOpenChange,
    children
}) => {
    const [open, setOpen] = useState(defaultOpen);
    const isOpen = forceOpen || (controlledOpen !== undefined ? controlledOpen : open);
    const sectionRef = useRef<HTMLDivElement>(null);

    const handleToggle = () => {
        const willOpen = !open;
        setOpen(willOpen);
        onOpenChange?.(willOpen);
        if (willOpen) {
            requestAnimationFrame(() => {
                sectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            });
        }
    };

    return (
        <SectionContainer ref={sectionRef}>
            <SectionHeader onClick={handleToggle}>
                <SectionLeft>
                    {!forceOpen && <SectionArrow $open={isOpen}>&#9654;</SectionArrow>}
                    <SectionTitle>{title}</SectionTitle>
                </SectionLeft>
                <SectionCount $isZero={count === 0}>{isBoolean ? (count > 0 ? '✓' : '—') : count > 0 ? count : '—'}</SectionCount>
            </SectionHeader>
            <SectionBody $collapsed={!isOpen}>
                {forceOpen ? (
                    children
                ) : (
                    <>
                        <ItemsList $tall={tall}>{children}</ItemsList>
                        {helpUrl && isOpen && count > 0 && (
                            <FooterGuideLink
                                href={onHelpClick ? undefined : helpUrl}
                                target={onHelpClick ? undefined : '_blank'}
                                rel={onHelpClick ? undefined : 'noopener'}
                                as={onHelpClick ? 'span' : undefined}
                                onClick={
                                    onHelpClick
                                        ? (e: React.MouseEvent) => {
                                              e.preventDefault();
                                              onHelpClick();
                                          }
                                        : undefined
                                }
                                style={onHelpClick ? { cursor: 'pointer' } : undefined}
                            >
                                {T._('Learn more', 'Admin')} &#8250;
                            </FooterGuideLink>
                        )}
                    </>
                )}
            </SectionBody>
        </SectionContainer>
    );
};
